"use client"

import { ArrowLeftMini, ArrowRightMini } from "@medusajs/icons"
import { captureAnalyticsEvent } from "@/lib/analytics/posthog-client"
import { HttpTypes } from "@medusajs/types"
import { clx, IconButton } from "@medusajs/ui"
import Image from "next/image"
import { useCallback, useEffect, useMemo, useState } from "react"

type ImageGalleryProps = {
  product: HttpTypes.StoreProduct
}

const ImageGallery = ({ product }: ImageGalleryProps) => {
  const thumbnail = product?.thumbnail
  const images = useMemo(() => product?.images || [], [product])

  const [selectedImage, setSelectedImage] = useState(
    images[0] || {
      url: thumbnail,
      id: "thumbnail",
    }
  )
  const [selectedImageIndex, setSelectedImageIndex] = useState(0)

  const handleArrowClick = useCallback(
    (direction: "left" | "right") => {
      if (
        images.length === 0 ||
        (selectedImageIndex === 0 && direction === "left") ||
        (selectedImageIndex === images.length - 1 && direction === "right")
      ) {
        return
      }

      if (direction === "left") {
        setSelectedImageIndex((prev) => prev - 1)
        setSelectedImage(images[selectedImageIndex - 1])
      } else {
        setSelectedImageIndex((prev) => prev + 1)
        setSelectedImage(images[selectedImageIndex + 1])
      }

      captureAnalyticsEvent("product_image_zoomed", {
        product_id: product.id,
        image_id: selectedImage.id,
        direction,
      })
    },
    [images, product.id, selectedImage.id, selectedImageIndex]
  )

  const handleImageClick = useCallback(
    (image: HttpTypes.StoreProductImage) => {
      setSelectedImage(image)
      setSelectedImageIndex(images.findIndex((img) => img.id === image.id))
      captureAnalyticsEvent("product_image_zoomed", {
        product_id: product.id,
        image_id: image.id,
        interaction: "thumbnail",
      })
    },
    [images, product.id]
  )

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (document.activeElement instanceof HTMLInputElement) {
        return
      }

      if (e.key === "ArrowLeft") {
        handleArrowClick("left")
      } else if (e.key === "ArrowRight") {
        handleArrowClick("right")
      }
    }

    window.addEventListener("keydown", handleKeyDown)

    return () => {
      window.removeEventListener("keydown", handleKeyDown)
    }
  }, [handleArrowClick])

  return (
    <div className="flex flex-col justify-end items-center bg-neutral-100 p-4 md:p-6 lg:p-8 pt-0 gap-4 md:gap-6 w-full h-full rounded-lg">
      {/* Main Image Container - Mobile optimized */}
      <div
        className="relative aspect-square md:aspect-[29/34] w-full overflow-hidden rounded-lg"
        id={selectedImage.id}
      >
        <div className="flex p-4 md:p-8 lg:p-12">
          {!!selectedImage.url && (
            <Image
              src={selectedImage.url}
              priority
              className="absolute inset-0 rounded-lg p-4 md:p-8 lg:p-20 overflow-visible object-contain"
              alt={(selectedImage.metadata?.alt as string) || product.title}
              fill
              sizes="(max-width: 640px) 100vw, (max-width: 768px) 50vw, (max-width: 1024px) 40vw, 30vw"
              onError={() => {
                captureAnalyticsEvent("image_load_failed", {
                  product_id: product.id,
                  image_id: selectedImage.id,
                  image_url: selectedImage.url,
                  surface: "product_gallery",
                })
              }}
            />
          )}
        </div>
      </div>
      
      {/* Navigation Controls - Mobile optimized */}
      <div className="flex flex-col md:flex-row gap-y-3 md:gap-y-0 justify-between w-full">
        {/* Arrow Controls - Mobile optimized */}
        {images.length > 1 && (
          <div className="flex flex-row gap-x-2 self-center md:self-auto justify-center">
            <IconButton
              disabled={selectedImageIndex === 0}
              className="rounded-full items-center justify-center h-10 w-10 md:h-8 md:w-8 bg-white shadow-md hover:shadow-lg transition-shadow"
              onClick={() => handleArrowClick("left")}
            >
              <ArrowLeftMini className="h-4 w-4" />
            </IconButton>
            <IconButton
              disabled={selectedImageIndex === images.length - 1}
              className="rounded-full items-center justify-center h-10 w-10 md:h-8 md:w-8 bg-white shadow-md hover:shadow-lg transition-shadow"
              onClick={() => handleArrowClick("right")}
            >
              <ArrowRightMini className="h-4 w-4" />
            </IconButton>
          </div>
        )}
        
        {/* Thumbnail Navigation - Mobile optimized */}
        <ul className="flex flex-row gap-x-2 md:gap-x-4 overflow-x-auto justify-center md:justify-start pb-2 md:pb-0">
          {images.map((image, index) => (
            <li
              key={image.id}
              className="flex aspect-square w-12 h-12 md:w-8 md:h-8 rounded-lg overflow-hidden cursor-pointer transition-opacity hover:opacity-100 flex-shrink-0"
              onClick={() => handleImageClick(image)}
              role="button"
            >
              <Image
                src={image.url}
                alt={(image.metadata?.alt as string) || product.title}
                height={48}
                width={48}
                className={clx(
                  "object-cover w-full h-full",
                  index === selectedImageIndex 
                    ? "opacity-100 ring-2 ring-blue-500" 
                    : "opacity-60 hover:opacity-80"
                )}
                onError={() => {
                  captureAnalyticsEvent("image_load_failed", {
                    product_id: product.id,
                    image_id: image.id,
                    image_url: image.url,
                    surface: "product_gallery_thumbnail",
                  })
                }}
              />
            </li>
          ))}
        </ul>
      </div>
    </div>
  )
}

export default ImageGallery
